import { useEffect, useState, type FormEvent } from "react";
import type { Actuacion, Documento, Honorario } from "../../../shared/types";
import { Boton } from "../../components/Boton";
import { Campo, Input, Select } from "../../components/Formulario";
import { Modal } from "../../components/Modal";
import { Tabla, type ColumnaTabla } from "../../components/Tabla";
import { useToast } from "../../components/Toast";
import { api, ErrorApi } from "../../lib/apiClient";

// Mismo whitelist de MIME types que valida el backend — ver MIME_PERMITIDOS
// en functions/_app/routes/documentos.ts. El atributo `accept` es sólo una
// ayuda de UX (el navegador filtra el selector de archivos); la validación
// real sigue ocurriendo en el servidor.
const ACCEPT_ARCHIVOS_DOCUMENTO =
  "application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/jpeg,image/png,image/webp,text/plain";

function formatearPeso(bytes: number | null): string {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function PestanaDocumentos({
  expedienteId,
  documentos,
  onCambio,
}: {
  expedienteId: string;
  documentos: Documento[];
  onCambio: () => void;
}) {
  const { mostrar } = useToast();
  const [modalAbierto, setModalAbierto] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [titulo, setTitulo] = useState("");
  const [archivo, setArchivo] = useState<File | null>(null);
  const [actuacionId, setActuacionId] = useState("");
  const [honorarioId, setHonorarioId] = useState("");
  const [documentoBaseId, setDocumentoBaseId] = useState("");
  const [actuaciones, setActuaciones] = useState<Actuacion[]>([]);
  const [honorarios, setHonorarios] = useState<Honorario[]>([]);

  useEffect(() => {
    api
      .get<{ items: Actuacion[] }>(`/actuaciones?expediente_id=${expedienteId}`)
      .then((d) => setActuaciones(d.items))
      .catch(() => undefined);
    api
      .get<{ items: Honorario[] }>(`/honorarios?expediente_id=${expedienteId}`)
      .then((d) => setHonorarios(d.items))
      .catch(() => undefined);
  }, [expedienteId]);

  function limpiar() {
    setTitulo("");
    setArchivo(null);
    setActuacionId("");
    setHonorarioId("");
    setDocumentoBaseId("");
  }

  async function manejarEnvio(e: FormEvent) {
    e.preventDefault();
    if (!archivo) {
      mostrar("Seleccioná un archivo.", "error");
      return;
    }
    setSubiendo(true);
    try {
      const formData = new FormData();
      formData.append("archivo", archivo);
      formData.append("titulo", titulo);
      formData.append("expediente_id", expedienteId);
      if (actuacionId) formData.append("actuacion_id", actuacionId);
      if (honorarioId) formData.append("honorario_id", honorarioId);
      if (documentoBaseId) formData.append("documento_base_id", documentoBaseId);
      await api.subirArchivo("/documentos", formData);
      mostrar("Documento subido correctamente.", "exito");
      setModalAbierto(false);
      limpiar();
      onCambio();
    } catch (err) {
      mostrar(
        err instanceof ErrorApi
          ? err.message
          : "No se pudo subir el documento.",
        "error",
      );
    } finally {
      setSubiendo(false);
    }
  }

  const columnas: ColumnaTabla<Documento>[] = [
    { encabezado: "Título", render: (d) => d.titulo },
    { encabezado: "Archivo", render: (d) => d.nombre_archivo },
    { encabezado: "Versión", render: (d) => `v${d.version}` },
    { encabezado: "Peso", render: (d) => formatearPeso(d.peso_bytes) },
    {
      encabezado: "Actuación",
      // El vínculo documentos.actuacion_id ya existía, pero era invisible
      // acá: no había forma de saber a qué actuación pertenecía un
      // documento subido desde esa pestaña (ver PestanaActuaciones.tsx,
      // que ahora muestra el mismo vínculo en la dirección inversa).
      render: (d) => {
        const act = actuaciones.find((a) => a.id === d.actuacion_id);
        return act ? `${act.tipo} — ${act.fecha}` : "—";
      },
    },
    {
      encabezado: "Confidencial",
      render: (d) => (d.confidencial ? "Sí" : "No"),
    },
    {
      encabezado: "Acciones",
      render: (d) => (
        <a
          href={`/api/documentos/${d.id}/descargar`}
          className="font-medium text-texto transition-colors hover:text-secundario hover:underline"
          target="_blank"
          rel="noreferrer"
        >
          Descargar
        </a>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <Boton tamano="sm" onClick={() => setModalAbierto(true)}>
          + Subir Documento
        </Boton>
      </div>
      <Tabla
        columnas={columnas}
        filas={documentos}
        claveFila={(d) => d.id}
        tituloVacio="Sin documentos"
        descripcionVacio="Subí el primer documento de este expediente."
      />
      <Modal
        titulo="Subir documento"
        abierto={modalAbierto}
        onCerrar={() => setModalAbierto(false)}
      >
        <form onSubmit={manejarEnvio} className="space-y-4">
          <Campo etiqueta="Título" obligatorio>
            <Input
              required
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
            />
          </Campo>
          <Campo etiqueta="Archivo" obligatorio>
            <input
              type="file"
              required
              accept={ACCEPT_ARCHIVOS_DOCUMENTO}
              onChange={(e) => setArchivo(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-texto-secundario file:mr-3 file:rounded-lg file:border-0 file:bg-principal file:px-3 file:py-2 file:text-sm file:font-medium file:text-superficie hover:file:bg-principal/90"
            />
          </Campo>
          {documentos.length > 0 && (
            <Campo etiqueta="¿Es una nueva versión de un documento existente?">
              <Select value={documentoBaseId} onChange={(e) => setDocumentoBaseId(e.target.value)}>
                <option value="">No, es un documento nuevo</option>
                {documentos.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.titulo} {d.version > 1 ? `(v${d.version})` : ""}
                  </option>
                ))}
              </Select>
            </Campo>
          )}
          {(actuaciones.length > 0 || honorarios.length > 0) && (
            <div className="grid grid-cols-2 gap-4">
              {actuaciones.length > 0 && (
                <Campo etiqueta="Actuación relacionada">
                  <Select value={actuacionId} onChange={(e) => setActuacionId(e.target.value)}>
                    <option value="">Ninguna</option>
                    {actuaciones.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.tipo} — {a.fecha}
                      </option>
                    ))}
                  </Select>
                </Campo>
              )}
              {honorarios.length > 0 && (
                <Campo etiqueta="Honorario relacionado">
                  <Select value={honorarioId} onChange={(e) => setHonorarioId(e.target.value)}>
                    <option value="">Ninguno</option>
                    {honorarios.map((h) => (
                      <option key={h.id} value={h.id}>
                        {h.tipo_honorario ?? h.modalidad}
                      </option>
                    ))}
                  </Select>
                </Campo>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Boton
              type="button"
              variante="secundario"
              onClick={() => setModalAbierto(false)}
              disabled={subiendo}
            >
              Cancelar
            </Boton>
            <Boton type="submit" cargando={subiendo}>
              Subir
            </Boton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
